import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import useAuthStore from '../../stores/useAuthStore'
import useNotificationStore from '../../stores/useNotificationStore'
import useSyncStore from '../../stores/useSyncStore'
import {
  UserCircleIcon,
  BellIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline'
import ConfirmDialog from '../common/ConfirmDialog'
import SyncStatusIndicator from '../common/SyncStatusIndicator'

/**
 * AppHeader Component
 * Beautiful fixed header with user avatar, name, notifications, and dropdown menu
 * Mobile-optimized with gradient background and glassmorphism effect
 */
const AppHeader = () => {
  const { user, logout } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()
  const [showDropdown, setShowDropdown] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)

  // Notification store
  const {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    formatTime,
    getNotificationColor
  } = useNotificationStore()

  // Sync store
  const {
    lastSyncTime,
    isSyncing,
    performSync
  } = useSyncStore()

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications()
  }, [])

  // Handle manual sync
  const handleSync = async () => {
    const result = await performSync()
    if (result.success) {
      // Optionally show success message
      console.log('[AppHeader] Sync successful:', result.message)
    } else {
      // Optionally show error message
      console.error('[AppHeader] Sync failed:', result.error)
    }
  }

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname
    if (path === '/') return 'لوحة التحكم'
    if (path.startsWith('/invoices')) return 'الفواتير'
    if (path.startsWith('/customers')) return 'العملاء'
    if (path.startsWith('/inventory')) return 'المخزون'
    if (path.startsWith('/products')) return 'المنتجات'
    if (path.startsWith('/collections')) return 'التحصيلات'
    if (path.startsWith('/returns')) return 'المرتجعات'
    if (path.startsWith('/profile')) return 'الملف الشخصي'
    return 'التطبيق'
  }

  // Handle notification click
  const handleNotificationClick = async (notif) => {
    if (!notif.read_at) {
      await markAsRead(notif.id)
    }
    // Could navigate to related page here
    setShowNotifications(false)
  }

  const handleLogout = () => {
    logout()
    setShowDropdown(false)
  }

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (!user?.name) return 'M'
    return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  return (
    <>
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 shadow-lg">
        {/* Glass Effect Overlay */}
        <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>

        {/* Header Content */}
        <div className="relative">
          {/* Top Section - User Info & Notifications */}
          <div className="px-4 py-3 flex items-center justify-between">
            {/* Left - User Profile */}
            <div className="flex items-center space-x-3 space-x-reverse">
              {/* User Avatar */}
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="relative group"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white font-bold text-lg shadow-lg ring-4 ring-white/30 group-hover:ring-white/50 transition-all duration-300 group-hover:scale-105">
                  {getUserInitials()}
                </div>
                {/* Online Indicator */}
                <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-400 rounded-full border-2 border-white shadow-lg"></div>
              </button>

              {/* User Info */}
              <div className="text-white">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center space-x-1 space-x-reverse group"
                >
                  <h2 className="font-bold text-base leading-tight group-hover:text-yellow-200 transition-colors">
                    {user?.name || 'مندوب المبيعات'}
                  </h2>
                  <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${showDropdown ? 'rotate-180' : ''}`} />
                </button>
                <p className="text-xs text-blue-100 opacity-90">
                  {user?.role === 'rep' ? 'مندوب مبيعات' : user?.role === 'manager' ? 'مدير' : 'مسؤول'}
                </p>
              </div>
            </div>

            {/* Right - Sync Status, Notifications & Menu */}
            <div className="flex items-center space-x-2 space-x-reverse">
              {/* Sync Status Indicator */}
              <div className="relative">
                <SyncStatusIndicator
                  onSync={handleSync}
                  lastSyncTime={lastSyncTime}
                  isSyncing={isSyncing}
                />
              </div>

              {/* Notifications Button */}
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 hover:bg-white/20 rounded-full transition-all duration-300 active:scale-95"
              >
                <BellIcon className="w-6 h-6 text-white" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg animate-pulse">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Menu Button */}
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="p-2 hover:bg-white/20 rounded-full transition-all duration-300 active:scale-95"
              >
                {showDropdown ? (
                  <XMarkIcon className="w-6 h-6 text-white" />
                ) : (
                  <Bars3Icon className="w-6 h-6 text-white" />
                )}
              </button>
            </div>
          </div>

          {/* Bottom Section - Page Title */}
          <div className="px-4 pb-3">
            <div className="flex items-center justify-between">
              <h1 className="text-white font-bold text-xl">
                {getPageTitle()}
              </h1>
              <div className="text-white/80 text-sm">
                {new Date().toLocaleDateString('ar-EG', { weekday: 'short', day: 'numeric', month: 'short' })}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Dropdown Menu */}
      {showDropdown && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 animate-fadeIn"
            onClick={() => setShowDropdown(false)}
          ></div>

          {/* Menu Content */}
          <div className="fixed top-24 right-4 w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl z-50 overflow-hidden animate-slideDown">
            {/* User Info Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4">
              <div className="flex items-center space-x-3 space-x-reverse">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white font-bold text-lg">
                  {getUserInitials()}
                </div>
                <div className="text-white">
                  <h3 className="font-bold text-sm">{user?.name}</h3>
                  <p className="text-xs opacity-90">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="p-2">
              <Link
                to="/profile"
                onClick={() => setShowDropdown(false)}
                className="flex items-center space-x-3 space-x-reverse px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
              >
                <UserCircleIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                <span className="text-gray-700 dark:text-gray-200">الملف الشخصي</span>
              </Link>

              <button
                onClick={() => {
                  setShowLogoutConfirm(true)
                  setShowDropdown(false)
                }}
                className="w-full flex items-center space-x-3 space-x-reverse px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors text-red-600 dark:text-red-400"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
                <span>تسجيل الخروج</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Logout Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
        title="تسجيل الخروج"
        message="هل أنت متأكد من تسجيل الخروج؟"
        confirmText="تسجيل الخروج"
        cancelText="إلغاء"
        variant="danger"
      />

      {/* Notifications Panel */}
      {showNotifications && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 animate-fadeIn"
            onClick={() => setShowNotifications(false)}
          ></div>

          {/* Notifications Content */}
          <div className="fixed top-24 left-4 right-4 max-w-md mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-2xl z-50 overflow-hidden animate-slideDown max-h-96 overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 sticky top-0 flex items-center justify-between">
              <h3 className="text-white font-bold">الإشعارات</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-white/90 hover:text-white text-xs underline"
                >
                  تعليم الكل كمقروء
                </button>
              )}
            </div>

            {/* Notifications List */}
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <div className="p-8 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-2 text-sm">جاري التحميل...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <BellIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>لا توجد إشعارات</p>
                </div>
              ) : (
                notifications.map(notif => (
                  <button
                    key={notif.id}
                    className="w-full p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-right"
                    onClick={() => handleNotificationClick(notif)}
                  >
                    <div className="flex items-start space-x-3 space-x-reverse">
                      {!notif.read_at && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className={`font-semibold text-sm ${!notif.read_at ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}>
                          {notif.data?.title || 'إشعار'}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                          {notif.data?.message || notif.data?.body || ''}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          {formatTime(notif.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* View All Button */}
            {notifications.length > 0 && (
              <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <button
                  onClick={() => {
                    setShowNotifications(false)
                    navigate('/notifications')
                  }}
                  className="w-full py-2 text-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold text-sm transition-colors"
                >
                  عرض جميع الإشعارات ({notifications.length})
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Spacer for fixed header */}
      <div className="h-28"></div>

      {/* Custom Animations - Moved to index.css */}
    </>
  )
}

export default AppHeader
